/**
 * SMS phone verification API — `/admin/sms-verification/...` on the px backend.
 *
 * KYC step 4: the user sends a one-time code by SMS from their own phone to one
 * of our Twilio / Telnyx numbers, and the provider's webhook tells us who sent
 * it. Everything is configured here — provider credentials (write-only: the
 * backend only ever returns `••••a91f`), inbound numbers, the countries the
 * step is on for, and the attempt limits / blocks.
 */

const getApiBase = () => {
  const base = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
  return base.endsWith("/admin") ? `${base}/sms-verification` : `${base}/admin/sms-verification`;
};

const headers = () => {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const handleResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status === false) {
    const err = new Error(data?.message || data?.error || `Request failed with status ${response.status}`);
    err.response = data;
    throw err;
  }
  return data;
};

const request = async (method, path, body) => {
  const res = await fetch(`${getApiBase()}${path}`, {
    method,
    headers: headers(),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  return handleResponse(res);
};

export const PROVIDERS = [
  { key: "twilio", label: "Twilio" },
  { key: "telnyx", label: "Telnyx" },
];
export const providerLabel = (key) => PROVIDERS.find((p) => p.key === key)?.label || key;

/** The admin-editable limits, in the order the form shows them. Bounds come from the backend (`limits`). */
export const LIMIT_FIELDS = [
  { key: "challengeTtlMinutes", label: "Code valid for", unit: "minutes" },
  { key: "retryCooldownMinutes", label: "Wait before a new code", unit: "minutes" },
  { key: "maxChallengesPerDay", label: "Max codes per user per day", unit: "codes" },
  { key: "maxFailedAttempts", label: "Failed tries before a block", unit: "tries" },
  { key: "firstBlockHours", label: "First block", unit: "hours" },
  { key: "repeatBlockHours", label: "Every later block", unit: "hours" },
  { key: "maxBlocksBeforeReview", label: "Blocks before admin review (0 = never)", unit: "blocks" },
  { key: "maxChallengesPerPhonePerDay", label: "Max codes per phone number per day", unit: "codes" },
  { key: "maxChallengesPerDevicePerDay", label: "Max codes per device per day", unit: "codes" },
];

/** What each webhook outcome means, for the event log. */
export const REASON_LABELS = {
  verified: "Verified",
  sender_mismatch: "Code sent from another phone",
  wrong_code: "Wrong code",
  expired: "Code had expired",
  phone_taken: "Number already used by another account",
  already_verified: "User already verified",
  code_not_active: "Code already used / closed",
  wrong_destination: "Sent to a different number of ours",
  unknown_number: "Not one of our numbers (for this provider)",
  number_not_receiving: "Number switched off",
  invalid_sender: "Sender is not a phone number",
  no_matching_code: "No matching code",
  no_code: "No code in the message",
  not_configured: "Encryption key missing",
};
export const reasonLabel = (reason) => REASON_LABELS[reason] || reason || "—";

/** `5400` → `1h 30m`. */
export const formatDuration = (seconds) => {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (s < 60) return `${s}s`;
  const m = Math.ceil(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (h < 48) return rest ? `${h}h ${rest}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d}d ${h % 24}h` : `${d}d`;
};

export const fetchSmsConfig = () => request("GET", "/config");
export const saveSmsSettings = (body) => request("PUT", "/settings", body);

export const saveProviderCredentials = (provider, body) => request("PUT", `/providers/${encodeURIComponent(provider)}`, body);
export const clearProviderCredentials = (provider) => request("DELETE", `/providers/${encodeURIComponent(provider)}`);
export const testProvider = (provider) => request("POST", `/providers/${encodeURIComponent(provider)}/test`);
/** Adds the account's SMS-capable numbers that are not listed yet (also runs on every credential save). */
export const importProviderNumbers = (provider) => request("POST", `/providers/${encodeURIComponent(provider)}/import`);

export const createInboundNumber = (body) => request("POST", "/numbers", body);
export const updateInboundNumber = (id, body) => request("PUT", `/numbers/${encodeURIComponent(id)}`, body);
export const deleteInboundNumber = (id) => request("DELETE", `/numbers/${encodeURIComponent(id)}`);

export const saveCountries = (body) => request("PUT", "/countries", body);

export const fetchWebhookEvents = ({ provider = "", status = "", page = 1, limit = 25 } = {}) => {
  const query = new URLSearchParams();
  if (provider) query.append("provider", provider);
  if (status) query.append("status", status);
  query.append("page", page);
  query.append("limit", limit);
  return request("GET", `/events?${query.toString()}`);
};

export const fetchPhoneUsers = ({ filter = "blocked", search = "", page = 1, limit = 25 } = {}) => {
  const query = new URLSearchParams();
  query.append("filter", filter);
  if (search) query.append("search", search.trim());
  query.append("page", page);
  query.append("limit", limit);
  return request("GET", `/users?${query.toString()}`);
};

export const fetchPhoneUser = (userId) => request("GET", `/users/${encodeURIComponent(userId)}`);
export const unblockPhoneUser = (userId, body = {}) => request("POST", `/users/${encodeURIComponent(userId)}/unblock`, body);
export const resetPhoneUser = (userId) => request("POST", `/users/${encodeURIComponent(userId)}/reset`);
